import { FC, useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import axios from 'axios';
import { searchCartByCode } from '../services/PickingCRUD';
import { ReprintLabelButton } from './ReprintLabelButton';
import { LAST_LABEL_STORAGE_KEY, CART_CODE_LENGTH } from '../constants/picking';

const HTTP_NOT_FOUND = 404;

const CartCodeInput: FC = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const lastLabelUrl = localStorage.getItem(LAST_LABEL_STORAGE_KEY);
  const inputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const intl = useIntl();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearch = async (searchCode: string) => {
    if (searchCode.length !== CART_CODE_LENGTH) return;

    setLoading(true);
    setError('');
    try {
      await searchCartByCode(searchCode);
      history.push(`/picking/${searchCode.toUpperCase()}`);
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const message =
        status === HTTP_NOT_FOUND
          ? intl.formatMessage({ id: 'picking.cartNotFound' })
          : intl.formatMessage({ id: 'picking.searchError' });
      setError(message);
      toast.error(message);
      setCode('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, CART_CODE_LENGTH);
    setCode(value);
    setError('');
    if (value.length === CART_CODE_LENGTH) {
      handleSearch(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(code);
  };

  return (
    <div className="card p-5" style={{ maxWidth: 400, width: '100%' }}>
      <h2 className="text-center mb-4">
        {intl.formatMessage({ id: 'picking.scanCart' })}
      </h2>
      <form onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          className={`form-control form-control-lg text-center mb-3 ${error ? 'is-invalid' : ''}`}
          style={{ fontSize: 32, letterSpacing: 8, fontWeight: 'bold' }}
          placeholder="____"
          aria-label={intl.formatMessage({ id: 'picking.cartCodePlaceholder' })}
          value={code}
          onChange={handleChange}
          maxLength={CART_CODE_LENGTH}
          autoComplete="off"
          disabled={loading}
        />
        {error && <div className="text-danger text-center mb-3">{error}</div>}
        <button
          type="submit"
          className="btn btn-primary btn-lg w-100"
          style={{ height: 56, fontSize: 18 }}
          disabled={code.length !== CART_CODE_LENGTH || loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              {intl.formatMessage({ id: 'picking.searching' })}
            </>
          ) : (
            intl.formatMessage({ id: 'picking.search' })
          )}
        </button>
      </form>
      {lastLabelUrl && (
        <ReprintLabelButton
          labelUrl={lastLabelUrl}
          className="btn btn-info w-100 mt-3"
          style={{ height: 56, fontSize: 18 }}
        />
      )}
    </div>
  );
};

export { CartCodeInput };
